import type { Pinia } from 'pinia'
import { createStorePersistence } from '~/cynqueModules/store/persistence'
import { setNuxtRuntime } from '~/cynqueModules/nuxtRuntime'

// Store initialisation. Pinia holds all 84 stores (`stores/<name>.ts`); Vuex was
// removed in P4a, and the `$store` compatibility facade in P4b — every caller
// now imports the store it needs (`useXStore()`), or resolves one by name
// through piniaStoreRegistry when the namespace is only known at runtime.
//
// All this plugin still does is wire up persistence and capture the router. The
// `00.` filename prefix keeps it the first user plugin to run, so persistence is
// registered before anything can instantiate a store (a Pinia plugin only
// applies to stores created after `pinia.use()`).
export default defineNuxtPlugin((nuxtApp) => {
  // @pinia/nuxt installs Pinia and exposes it here. Its plugin is registered by
  // the module, so it has already run by the time user plugins do; fail loudly
  // rather than silently leaving every store unpersisted.
  const pinia = nuxtApp.$pinia as Pinia | undefined
  if (!pinia) {
    throw new Error(
      '[plugins/00.store] Pinia is not installed — @pinia/nuxt must be in nuxt.config modules'
    )
  }

  // Reads window storage, so client only — the app is SPA, so plugins run
  // client-side regardless, but keep the guard explicit. A Pinia plugin only
  // applies to stores created AFTER `pinia.use()`, so a store instantiated ahead
  // of this line would never hydrate and never persist.
  if (import.meta.client) {
    const persistence = createStorePersistence({
      localStorage: window.localStorage,
      sessionStorage: window.sessionStorage,
    })
    pinia.use(persistence.piniaPlugin)
  }

  // Register the router as a client-side runtime singleton so non-component code
  // (services, error handlers, the async-operation resumer) can reach it without
  // the removed Nuxt-2 `window.$nuxt` global. See cynqueModules/nuxtRuntime.ts.
  setNuxtRuntime(useRouter())
})
